import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp, EASE } from "./Reveal";

const SERVICES = [
  {
    title: "General Construction",
    desc: "Ground-up builds, additions and structural work executed with zero shortcuts. We manage permits, trades and timelines so the only thing you watch is progress.",
    tags: ["New Builds", "Additions", "Structural", "Framing"],
    img: "https://images.unsplash.com/photo-1768948166165-b3b332618a08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxjaW5lbWF0aWMlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZGFya3xlbnwwfHx8fDE3ODc1ODI2MzB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Commercial Contracting",
    desc: "Tenant improvements, fit-outs and light commercial builds delivered on schedule and to code. One contract, one accountable crew — from permit to handover.",
    tags: ["Tenant Fit-Outs", "Build-Outs", "Light Commercial", "Code & Permits"],
    img: "https://customer-assets-agu9un31.emergentagent.net/job_northedge-pro/artifacts/jn6osqcp_IMG_8733.webp",
  },
  {
    title: "Home Renovation & Remodeling",
    desc: "Kitchens, basements, bathrooms and full-home transformations. Dust-controlled sites, honest schedules, and finishes that hold up to real life.",
    tags: ["Kitchens", "Basements", "Bathrooms", "Whole-Home"],
    img: "https://images.pexels.com/photos/36511379/pexels-photo-36511379.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    title: "Concrete",
    desc: "Driveways, garage pads, stamped patios and foundations poured for northern freeze-thaw cycles. Reinforced, sealed and finished by hand.",
    tags: ["Driveways", "Stamped", "Flatwork", "Foundations"],
    img: "https://images.pexels.com/photos/17729218/pexels-photo-17729218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    title: "Snow Removal",
    desc: "24/7 dispatch, GPS-tracked fleets and seasonal contracts that guarantee your lot is clear before your first coffee. Residential routes to commercial campuses.",
    tags: ["24/7 Dispatch", "Seasonal Contracts", "Commercial Lots", "Salting & Sanding"],
    img: "https://images.pexels.com/photos/11680712/pexels-photo-11680712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const Services = () => {
  const [open, setOpen] = useState(3);

  return (
    <section id="services" className="py-24 sm:py-32 lg:py-48" data-testid="services-section">
      <div className="max-w-[1800px] mx-auto px-6 sm:px-12 lg:px-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16 lg:mb-24">
          <div>
            <FadeUp>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3B00] mb-8">What we do</p>
            </FadeUp>
            <h2 className="font-oswald font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[0.95]" data-testid="services-heading">
              <MaskedLine>Five trades.</MaskedLine>
              <MaskedLine delay={0.12}>
                <span className="text-outline">One standard.</span>
              </MaskedLine>
            </h2>
          </div>
          <FadeUp delay={0.2}>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#808080] max-w-xs leading-relaxed">
              Tap a service to open the spec sheet
            </p>
          </FadeUp>
        </div>

        <div className="border-b border-[#262626]">
          {SERVICES.map((s, i) => {
            const isOpen = open === i;
            return (
              <div key={s.title} className="border-t border-[#262626]">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className={`group w-full flex items-center gap-5 sm:gap-10 py-8 sm:py-10 text-left px-2 sm:px-4 transition-colors duration-500 ${
                    isOpen ? "bg-[#121212]" : "hover:bg-[#121212]"
                  }`}
                  data-testid={`service-row-${i + 1}`}
                >
                  <span className="font-mono text-sm text-[#FF3B00] w-8 shrink-0">0{i + 1}</span>
                  <span
                    className={`flex-1 font-oswald font-bold uppercase tracking-tight text-2xl sm:text-4xl lg:text-6xl leading-none transition-colors duration-500 ${
                      isOpen ? "text-[#FF3B00]" : "text-[#F2F2F2] group-hover:text-[#FF3B00]"
                    }`}
                  >
                    {s.title}
                  </span>
                  <ArrowUpRight
                    className={`h-6 w-6 sm:h-9 sm:w-9 shrink-0 transition-all duration-500 ${
                      isOpen ? "rotate-90 text-[#FF3B00]" : "text-[#3A3A3A] group-hover:text-[#FF3B00] group-hover:translate-x-1 group-hover:-translate-y-1"
                    }`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.7, ease: EASE }}
                      className="overflow-hidden bg-[#121212]"
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 px-2 sm:px-4 pb-12 lg:pl-[4.5rem]">
                        <div className="lg:col-span-7">
                          <p className="text-base sm:text-lg font-light leading-relaxed text-[#B0B0B0] max-w-2xl">
                            {s.desc}
                          </p>
                          <div className="mt-8 flex flex-wrap gap-3">
                            {s.tags.map((t) => (
                              <span
                                key={t}
                                className="border border-[#262626] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#808080] hover:border-[#FF3B00] hover:text-[#FF3B00] transition-colors duration-300"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="lg:col-span-5">
                          <div className="clip-frame spotlight relative h-56 lg:h-72 overflow-hidden border border-[#262626]">
                            <img
                              src={s.img}
                              alt={s.title}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
